import React, { useEffect, useRef, useState} from "react";

/*************************************************************************
 * Selector Component
 * 
 * A reusable dropdown selector that uses provided options.
 * It normalizes options to ensure consistency and handles selection changes.
 * Utilizes React.memo for performance optimization by preventing unnecessary re-renders.
 * 
 * @param {Object} props
 * @param {string} props.label - The text to display as the label for the selector.
 * @param {string} props.id - The unique identifier for the select element.
 * @param {Array} [props.options=[]] - Predefined options to use in the selector.
 * @param {string} props.value - The currently selected value.
 * @param {Function} props.onChange - Callback function to handle value changes.
 * @param {boolean} [props.disabled=false] - Whether the selector is disabled.
 */
const Selector = React.memo(({ 
  label, 
  id, 
  options = [],
  value, 
  onChange, 
  disabled = false 
}) => {

  /*************************************************************************
   * Inputs can come with various names for the value field.
   * This function normalizes the options to ensure consistency.
   * All values are converted to 'id' and 'value' fields.
   * 
   * @param {Object} option - The option object to normalize.
   * @returns {Object} - Normalized option with 'id' and 'value'.
   */
  const normalizeOption = (option) => {
    if (option.email) return { id: option.id, value: option.email }; // Treatment for "email"
    if (option.name) return { id: option.id, value: option.name }; // Treatment pour "opportunity"
  };

  // console.log(label, "Selector - options :", options);

  // Normalize provided options or set to empty array if not provided
  const safeOptions = Array.isArray(options) ? options.map(normalizeOption) : [];

  // console.log(label, "Selector - safeOptions :", safeOptions);

  // Determine the current value or default to empty string
  const safeValue = value || "";

  /*************************************************************************
   * Handles changes in the select element.
   * Finds the selected option and triggers the onChange callback.
   * 
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event from the select element.
   */
  const handleChange = (e) => {
    const selectedValue = e.target.value;
    const selectedOption = safeOptions.find(opt => opt.value === selectedValue);
    if (selectedOption) {
      onChange({ id: selectedOption.id, value: selectedOption.value });
    }
  };

  return (
    <div>
      {/* Label for the select element */}
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {/* Select dropdown */}
      <select
        id={id}
        onChange={handleChange}
        value={safeValue}
        disabled={disabled || safeOptions.length === 0}
        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
        focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      >
        {/* Placeholder option */}
        <option value="">
          {safeOptions.length > 0 ? `Select ${label}` : `No ${label} available`}
          </option>

        {/* Dynamically generated options */}
        {safeOptions.map((option) => (
          <option 
            key={option.id}
            value={option.value}
          >
            {option.value}
          </option>
        ))}
      </select>

      {/* <p>Container Width: {size.width}px</p>
      <p>Container Height: {size.height}px</p> */}

    </div>
  );
});

export default Selector;
